import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import type { AddonConfig } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { reasonProblem } from './addonReason';
import { enableAddonCallable } from '@/lib/callables';

type Props = {
  addon: AddonConfig | null;
  onClose: () => void;
};

/**
 * Confirma a retomada da venda de um add-on pausado (`enableAddon`).
 *
 * Origem: #710 — 2026-09-24 13:31 +0200 (F3 da #679, D20).
 *
 * Por quê: retomar a venda é o que torna o módulo comprável de novo; lembra
 * que, sem piso no CRM, ele continua invendável na Contratação.
 *
 * Contrato: chama `enableAddonCallable({ sku, reason })`; toast de sucesso e
 * fecha, ou `billing.addons.errors.enable` num `Alert` dentro do modal (#1021). O confirmar só habilita com
 * motivo válido (10–500 após `trim`, `reasonProblem`).
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageAddons` (ROOT/FINANCE) e grava
 * em `config/addons/items/{sku}` (rules `match /config/addons/items/{sku}`).
 *
 * Usado por: `routes/AddonsPage.tsx` (rota `/billing/addons`, menu Cobrança →
 * Add-ons; spec `docs/screens/billing-addons.md`), botão "Retomar venda" do card.
 *
 * Falhas conhecidas:
 *  - 2026-09-29 (#710): confirmar habilitado com motivo inválido; acima de
 *    500 caracteres o erro era o genérico. Ver comentário em `problem`.
 *
 * Issues: #679 (épico), #710 (pausar/retomar venda).
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu, permissão e doc do `submit` (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function EnableAddonDialog({ addon, onClose }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  // Bug corrigido em 2026-09-29 18:38 +0200 (#710): o confirmar ficava
  // habilitado com motivo inválido — o mínimo só era checado no clique (erro
  // em toast) e acima de 500 o `callable()` recusava com o erro genérico. Agora
  // o botão só habilita com o motivo válido pelo mesmo schema do callable, e o
  // erro aparece no campo. Não voltar a validar só no submit.
  const problem = reasonProblem(reason);
  const showProblem = problem === 'long' || (problem === 'short' && touched);

  useEffect(() => {
    if (addon) {
      setReason('');
      setError(null);
      setTouched(false);
    }
  }, [addon]);

  /**
   * Envia `{ sku, reason }` ao `enableAddon` e fecha com toast de sucesso.
   *
   * Origem: #710 — 2026-09-24 13:31 +0200.
   *
   * Contrato: no-op sem add-on ou com motivo inválido (defesa; o botão já está
   * desabilitado). Input `{ "sku": "MODULE_X", "reason": "..." }`. Erro → `Alert`
   * (#1021) genérico `billing.addons.errors.enable` (sem a mensagem do servidor); o `err`
   * vai ao PostHog como `admin_addon_enable_failed` — até 2026-10-13 ia só ao
   * `console.error` (#798 → #935 item 14).
   *
   * Usado por: `onConfirm` do `AdminConfirm` deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   *  - 2026-10-27 12:43 +0100 — (#1043) recusa de SKU aposentado ganha texto próprio.
   */
  async function submit(): Promise<void> {
    if (!addon) return;
    // Defesa: o botão já fica desabilitado com motivo inválido.
    if (problem) return;
    setSubmitting(true);
    setError(null);
    try {
      await enableAddonCallable({ sku: addon.sku, reason: reason.trim() });
      toast.success(t('billing.addons.success.enabled'));
      onClose();
    } catch (err) {
      // #935 (item 14, 2026-10-13 11:00 +0200): só `console.*` não chega até nós — o admin
      // não manda DevTools. O report leva a falha ao PostHog (CLAUDE.md, Observabilidade).
      reportNonFatal('admin_addon_enable_failed', { sku: addon.sku, ...serializeError(err) });
      // #1043 (2026-10-27 12:43 +0100): SKU aposentado (`MOD_SCALE`) é recusado pelo servidor com
      // `details.reason = 'retired_sku'`; a mensagem diz o porquê, em vez do erro genérico.
      setError(t(isRetiredSkuRefusal(err) ? 'billing.addons.errors.enableRetired' : 'billing.addons.errors.enable'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AdminConfirm
      visible={addon !== null}
      title={addon ? t('billing.addons.dialog.enableTitle', { sku: addon.sku }) : ''}
      confirmLabel={t('billing.addons.dialog.enableConfirm')}
      cancelLabel={t('billing.addons.dialog.cancel')}
      loading={submitting}
      confirmDisabled={problem !== null}
      onConfirm={(): void => {
        void submit();
      }}
      onDismiss={onClose}
    >
      <SpaceBetween size="m">
        <Alert type="info">{t('billing.addons.dialog.enableWarning')}</Alert>
        <FormField
          label={t('billing.addons.dialog.enableReason')}
          errorText={
            showProblem
              ? t(problem === 'long' ? 'billing.addons.errors.reasonTooLong' : 'billing.addons.errors.enableReasonShort')
              : undefined
          }
        >
          <Input
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
            onBlur={(): void => {
              setTouched(true);
            }}
            placeholder={t('billing.addons.dialog.enableReasonPlaceholder')}
            disabled={submitting}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </AdminConfirm>
  );
}

/** A recusa do `enableAddon` é de SKU aposentado (#1043)? O callable manda `details.reason = 'retired_sku'`. */
function isRetiredSkuRefusal(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('details' in err)) return false;
  const details = (err as { details?: unknown }).details;
  return typeof details === 'object' && details !== null && (details as { reason?: unknown }).reason === 'retired_sku';
}
